// src/components/templates/diary/DiaryDashboard.jsx
import React, { useState } from 'react';
import { Card, Button, Badge, MeetingStatus, MinuteType } from './UIComponents';
import { 
  Calendar, Plus, Clock, ChevronRight, ChevronLeft, 
  CheckSquare, Bell, MapPin, Search, Users, FileText,
  TrendingUp, Activity, Target, BarChart3
} from 'lucide-react';

// --- MINI CALENDAR COMPONENT ---
const MiniCalendar = ({ meetings = [], onDateClick, customStyling={} }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const getMeetingStatusForDate = (dateStr) => {
    const dayMeetings = meetings.filter(m => m.date === dateStr);
    if (dayMeetings.some(m => m.status === MeetingStatus.ACTIVE)) return 'active';
    if (dayMeetings.some(m => m.status === MeetingStatus.PLANNED)) return 'planned';
    if (dayMeetings.some(m => m.status === MeetingStatus.COMPLETED)) return 'completed';
    return null;
  };

  const getMeetingCountForDate = (dateStr) => {
    return meetings.filter(m => m.date === dateStr).length;
  };

  const renderDays = () => {
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-8" />);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const status = getMeetingStatusForDate(dateStr);
      const count = getMeetingCountForDate(dateStr);
      
      let dotClass = '';
      if (status === 'active') dotClass = 'bg-green-500';
      else if (status === 'planned') dotClass = 'bg-indigo-500';
      else if (status === 'completed') dotClass = 'bg-gray-300';

      const isToday = new Date().toDateString() === new Date(year, month, d).toDateString();
      const isPast = new Date(year, month, d) < new Date(new Date().setHours(0,0,0,0));

      days.push(
        <div 
          key={d} 
          onClick={() => onDateClick?.(dateStr)}
          className={`h-8 w-8 mx-auto flex flex-col items-center justify-center rounded-full text-xs relative transition-all
             ${isToday ? 'bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-200' : 
               isPast ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-100'}
             ${count > 0 ? 'cursor-pointer' : 'cursor-default'}`}
        >
          {d}
          {status && <div className={`absolute bottom-0.5 w-1.5 h-1.5 rounded-full ${dotClass}`} />}
          {count > 1 && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center font-bold">{count}</span>}
        </div>
      );
    }
    return days;
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-sm"
          style={{ color: customStyling.primaryColor }}
        >
          {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h3>
        <div className="flex items-center space-x-1">
          <button onClick={goToToday} className="px-2 py-1 text-[10px] font-medium hover:bg-indigo-50 rounded"
            style={{ color: customStyling.primaryColor }}
          >Today</button>
          <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {['S','M','T','W','T','F','S'].map((d, i) => <span key={i} className="text-[10px] font-medium text-gray-400">{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {renderDays()}
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-500 justify-center">
        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-indigo-500 mr-1.5" /> Planned</span>
        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-green-500 mr-1.5" /> Active</span>
        <span className="flex items-center"><div className="w-2 h-2 rounded-full bg-gray-300 mr-1.5" /> Done</span>
      </div>
    </Card>
  );
};

// --- SIMPLE PIE CHART (No external deps) ---
const SimplePieChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
        <BarChart3 className="w-6 h-6 text-gray-300" />
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) {
    return (
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
        <span className="text-xs text-gray-400">0</span>
      </div>
    );
  }

  // Calculate percentages and angles
  let cumulativePercent = 0;
  const segments = data.map(d => {
    const percent = (d.value / total) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    return { ...d, percent, startPercent };
  });

  // Create conic-gradient
  const gradientParts = segments.map(s => 
    `${s.color} ${s.startPercent}% ${s.startPercent + s.percent}%`
  ).join(', ');

  return (
    <div className="relative w-16 h-16">
      <div 
        className="w-full h-full rounded-full"
        style={{ background: `conic-gradient(${gradientParts})` }}
      />
      <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
        <span className="text-xs font-bold text-gray-700">{total}</span>
      </div>
    </div>
  );
};

// --- STAT CARD COMPONENT ---
const StatCard = ({ title, value, icon: Icon, color, trend, trendLabel, onClick }) => {
  const colorClasses = {
    indigo: { border: 'border-l-indigo-500', bg: 'bg-indigo-50', text: 'text-indigo-600' },
    amber: { border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-600' },
    emerald: { border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-600' },
    purple: { border: 'border-l-purple-500', bg: 'bg-purple-50', text: 'text-purple-600' },
    rose: { border: 'border-l-rose-500', bg: 'bg-rose-50', text: 'text-rose-600' },
  };
  
  const c = colorClasses[color] || colorClasses.indigo;

  return (
    <Card 
      className={`p-6 border-l-4 ${c.border} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <h2 className="text-3xl font-bold text-gray-900 mt-1">{value}</h2>
          {trend !== undefined && (
            <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              <TrendingUp className={`w-3 h-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
              {Math.abs(trend)}% {trendLabel || 'vs last week'}
            </div>
          )}
        </div>
        <div className={`p-3 ${c.bg} rounded-xl`}>
          <Icon className={`w-6 h-6 ${c.text}`} />
        </div>
      </div>
    </Card>
  );
};

// --- QUICK ACTIONS COMPONENT ---
const QuickActions = ({ onCreateMeeting, customStyling, onNavigate }) => {
  const actions = [
    { label: 'New Meeting', icon: Plus, color: 'bg-indigo-600 hover:bg-indigo-700', action: onCreateMeeting },
    { label: 'View Calendar', icon: Calendar, color: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200', action: () => onNavigate?.('CALENDAR') },
    { label: 'All Meetings', icon: FileText, color: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200', action: () => onNavigate?.('MEETINGS') },
    { label: 'Contacts', icon: Users, color: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200', action: () => onNavigate?.('CONTACTS') },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((a, i) => (
        <button
          key={i}
          onClick={a.action}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${a.color} ${
            i === 0 ? 'text-white shadow-lg shadow-indigo-200' : ''
          }`}
          style={i === 0 ? { backgroundColor: customStyling.primaryColor } : {}}
        >
          <a.icon className="w-4 h-4 mr-2" />
          {a.label}
        </button>
      ))}
    </div>
  );
};

// --- MEETING LIST ITEM ---
const MeetingListItem = ({ meeting, onClick }) => {
  const statusColors = {
    [MeetingStatus.PLANNED]: { bg: 'bg-gray-100', text: 'text-gray-600' },
    [MeetingStatus.ACTIVE]: { bg: 'bg-indigo-600', text: 'text-white' },
    [MeetingStatus.COMPLETED]: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  };

  const colors = statusColors[meeting.status] || statusColors[MeetingStatus.PLANNED];
  const meetingDate = new Date(meeting.date);
  
  const taskCount = (meeting.minutes || []).filter(m => m.type === MinuteType.TASK && !m.completed).length;
  const agendaCount = (meeting.agenda || []).length;
  const participantCount = (meeting.participants || []).length;

  return (
    <div 
      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer group flex items-center justify-between"
      onClick={() => onClick?.(meeting)}
    >
      <div className="flex items-center space-x-4">
        <div className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 ${colors.bg} ${colors.text}`}>
          <span className="text-[10px] font-bold uppercase">{meetingDate.toLocaleString('default', { month: 'short' })}</span>
          <span className="text-xl font-bold leading-none">{meetingDate.getDate()}</span>
        </div>
        <div className="min-w-0">
          <h4 className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors truncate">{meeting.title}</h4>
          <div className="flex items-center text-sm text-gray-500 space-x-3 mt-1">
            <span className="flex items-center"><Clock className="w-3 h-3 mr-1"/> {meeting.time}</span>
            {meeting.location && <span className="flex items-center truncate max-w-[120px]"><MapPin className="w-3 h-3 mr-1 shrink-0"/> {meeting.location}</span>}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge color={
              meeting.status === MeetingStatus.PLANNED ? 'blue' :
              meeting.status === MeetingStatus.ACTIVE ? 'green' : 'gray'
            }>{meeting.status}</Badge>
            {participantCount > 0 && (
              <span className="text-[10px] text-gray-400 flex items-center"><Users className="w-3 h-3 mr-0.5" />{participantCount}</span>
            )}
            {agendaCount > 0 && (
              <span className="text-[10px] text-gray-400 flex items-center"><FileText className="w-3 h-3 mr-0.5" />{agendaCount}</span>
            )}
            {taskCount > 0 && (
              <span className="text-[10px] text-amber-600 flex items-center font-medium"><CheckSquare className="w-3 h-3 mr-0.5" />{taskCount}</span>
            )}
          </div>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-indigo-400 shrink-0" />
    </div>
  );
};

// --- REMINDER ITEM ---
const ReminderItem = ({ meeting, onClick }) => {
  const meetingDate = new Date(`${meeting.date}T${meeting.time || '00:00'}`);
  const now = new Date();
  const isToday = now.toDateString() === meetingDate.toDateString();
  const isTomorrow = new Date(now.getTime() + 86400000).toDateString() === meetingDate.toDateString();
  
  // Calculate time until meeting
  const diffMs = meetingDate.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let urgencyLabel = 'UPCOMING';
  let urgencyColor = 'bg-indigo-100 text-indigo-700';
  
  if (isToday) {
    if (diffHours <= 1 && diffHours >= 0) {
      urgencyLabel = 'STARTING SOON';
      urgencyColor = 'bg-red-100 text-red-700';
    } else {
      urgencyLabel = 'TODAY';
      urgencyColor = 'bg-amber-100 text-amber-700';
    }
  } else if (isTomorrow) {
    urgencyLabel = 'TOMORROW';
    urgencyColor = 'bg-purple-100 text-purple-700';
  }

  return (
    <div 
      className="p-4 hover:bg-indigo-50 transition-colors cursor-pointer group"
      onClick={() => onClick?.(meeting)}
    >
      <div className="flex justify-between items-start mb-2">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded tracking-wide ${urgencyColor}`}>
          {urgencyLabel}
        </span>
        <span className="text-xs text-gray-400">
          {diffDays > 0 ? `in ${diffDays}d` : diffHours > 0 ? `in ${diffHours}h` : 'now'}
        </span>
      </div>
      <h4 className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-700 leading-tight line-clamp-2">{meeting.title}</h4>
      <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2 mt-2">
        <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {meeting.time}</span>
        <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" /> {meeting.date}</span>
        {meeting.location && <span className="flex items-center truncate"><MapPin className="w-3 h-3 mr-1" /> {meeting.location}</span>}
      </div>
    </div>
  );
};

// --- TASK ITEM ---
const TaskItem = ({ task, meeting, onMeetingClick }) => {
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date();

  return (
    <div className={`p-3 hover:bg-gray-50 transition-colors ${task.completed ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-2">
        <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${task.completed ? 'bg-gray-300' : isOverdue ? 'bg-red-500' : 'bg-amber-500'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-gray-800 line-clamp-2 ${task.completed ? 'line-through' : ''}`}>{task.content}</p>
          <div className="flex justify-between items-center mt-2">
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isOverdue && !task.completed ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
              {task.completed ? 'Done' : isOverdue ? 'Overdue' : 'Action'}
            </span>
            <div className="flex items-center gap-2 text-[10px] text-gray-400">
              {task.dueDate && <span>Due: {task.dueDate}</span>}
              {meeting && (
                <button 
                  onClick={(e) => { e.stopPropagation(); onMeetingClick?.(meeting); }}
                  className="text-indigo-500 hover:text-indigo-700 hover:underline"
                >
                  {meeting.title?.slice(0, 15)}...
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN DASHBOARD COMPONENT ---
export const DiaryDashboard = ({ 
  meetings = [], 
  contacts = [], 
  customStyling,
  journalEntries = [], 
  onCreateMeeting, 
  onSelectMeeting,
  onNavigate
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Derived Data
  const upcomingMeetings = meetings.filter(m => m.status === MeetingStatus.PLANNED)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const activeMeetings = meetings.filter(m => m.status === MeetingStatus.ACTIVE);
  const completedMeetings = meetings.filter(m => m.status === MeetingStatus.COMPLETED);


  
  // All tasks across meetings
  const allTasks = meetings.flatMap(m => 
    (m.minutes || [])
      .filter(min => min.type === MinuteType.TASK)
      .map(task => ({ ...task, meetingId: m.id, meeting: m }))
  );
  const openTasks = allTasks.filter(t => !t.completed);
  const overdueTasks = openTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date());
  
  // Reminders: Top 5 upcoming meetings
  const reminders = upcomingMeetings.slice(0, 5);

  // Filter Logic
  const allSortedMeetings = [...activeMeetings, ...upcomingMeetings, ...completedMeetings];
  const filteredMeetings = allSortedMeetings.filter(m => {
    const query = searchQuery.toLowerCase();
    return (
      m.title.toLowerCase().includes(query) ||
      (m.location || '').toLowerCase().includes(query) ||
      m.date.includes(query)
    );
  });

  // Chart Data for simple pie
  const statusData = [
    { name: 'Planned', value: upcomingMeetings.length, color: '#6366f1' },
    { name: 'Active', value: activeMeetings.length, color: '#f59e0b' },
    { name: 'Completed', value: completedMeetings.length, color: '#10b981' },
  ].filter(d => d.value > 0);

  // Week stats
  const thisWeekStart = new Date();
  thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
  thisWeekStart.setHours(0, 0, 0, 0);
  
  const completedThisWeek = completedMeetings.filter(m => new Date(m.date) >= thisWeekStart).length;

  return (
    <div className="p-3 max-w-7xl mx-auto space-y-8">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            {activeMeetings.length > 0 
              ? `You have ${activeMeetings.length} meeting${activeMeetings.length > 1 ? 's' : ''} in progress.`
              : `Welcome back! You have ${upcomingMeetings.length} upcoming meeting${upcomingMeetings.length !== 1 ? 's' : ''}.`
            }
          </p>
        </div>
        <QuickActions onCreateMeeting={onCreateMeeting} onNavigate={onNavigate} customStyling={customStyling} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Planned Meetings" 
          value={upcomingMeetings.length} 
          icon={Calendar} 
          color="indigo"
          onClick={() => onNavigate?.('MEETINGS')}
        />
        <StatCard 
          title="Open Action Items" 
          value={openTasks.length} 
          icon={CheckSquare} 
          color="amber"
          onClick={() => onNavigate?.('NOTES')}
        />
        <StatCard 
          title="Completed This Week" 
          value={completedThisWeek} 
          icon={Target} 
          color="emerald"
        />
        {overdueTasks.length > 0 ? (
          <StatCard 
            title="Overdue Tasks" 
            value={overdueTasks.length} 
            icon={Activity} 
            color="rose"
            onClick={() => onNavigate?.('NOTES')}
          />
        ) : (
          <Card className="p-3 border-l-4 border-l-purple-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Contacts</p>
                <h2 className="text-3xl font-bold text-gray-900 mt-1">{contacts.length}</h2>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left: Meeting List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h3 className="text-lg font-semibold text-gray-800">Your Meetings</h3>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input 
                type="text" 
                placeholder="Search by title, date, location..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100 overflow-hidden">
            {meetings.length === 0 ? (
              <div className="p-8 text-center text-gray-500 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="w-8 h-8 text-gray-300" />
                </div>
                <p className="font-medium">No meetings yet</p>
                <p className="text-sm text-gray-400 mt-1">Create your first meeting to get started!</p>
                <Button onClick={onCreateMeeting} className="mt-4"
                  variant="primary"
                  style={{ backgroundColor: customStyling.primaryColor }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Meeting
                </Button>
              </div>
            ) : filteredMeetings.length === 0 ? (
              <div className="p-12 text-center text-gray-500 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-gray-300" />
                </div>
                <p className="font-medium">No matches found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              <>
                {filteredMeetings.slice(0, 8).map(meeting => (
                  <MeetingListItem key={meeting.id} meeting={meeting} onClick={onSelectMeeting} />
                ))}
                {filteredMeetings.length > 8 && (
                  <div className="p-3 bg-gray-50 text-center">
                    <button 
                      onClick={() => onNavigate?.('MEETINGS')}
                      className="text-sm text-indigo-600 font-medium hover:text-indigo-800"
                    >
                      View all {filteredMeetings.length} meetings
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Column: Calendar, Reminders & Tasks */}
        <div className="space-y-6">
          
          {/* Mini Calendar */}
          <MiniCalendar 
            meetings={meetings} 
            onDateClick={(date) => {
              const dayMeetings = meetings.filter(m => m.date === date);
              if (dayMeetings.length === 1) {
                onSelectMeeting?.(dayMeetings[0]);
              } else if (dayMeetings.length > 1) {
                onNavigate?.('CALENDAR');
              }
            }}
            customStyling={customStyling}
          />

          {/* Status Overview */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Meeting Overview</h3>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                {statusData.map(s => (
                  <div key={s.name} className="flex items-center text-xs">
                    <div className="w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                    <span className="text-gray-600">{s.name}</span>
                    <span className="ml-2 font-bold text-gray-800">{s.value}</span>
                  </div>
                ))}
                {statusData.length === 0 && (
                  <p className="text-xs text-gray-400">No meetings yet</p>
                )}
              </div>
              <SimplePieChart data={statusData} />
            </div>
          </Card>

          {/* Upcoming Reminders Widget */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-indigo-600" />
              Upcoming
            </h3>
            <Card className="p-0 border-t-4 border-t-indigo-500 overflow-hidden">
              <div className="divide-y divide-gray-100">
                {reminders.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 text-sm">
                    <div className="bg-gray-50 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-6 h-6 text-gray-300" />
                    </div>
                    <p className="font-medium">All clear!</p>
                    <p className="text-xs mt-1">No upcoming meetings</p>
                  </div>
                ) : (
                  reminders.map(meeting => (
                    <ReminderItem key={meeting.id} meeting={meeting} onClick={onSelectMeeting} />
                  ))
                )}
              </div>
              {upcomingMeetings.length > 5 && (
                <div className="p-2 bg-gray-50 text-center border-t border-gray-100">
                  <button 
                    className="text-xs text-indigo-600 font-medium hover:text-indigo-800"
                    onClick={() => onNavigate?.('MEETINGS')}
                  >
                    View all {upcomingMeetings.length} upcoming
                  </button>
                </div>
              )}
            </Card>
          </div>

          {/* Recent Tasks Widget */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <CheckSquare className="w-5 h-5 mr-2 text-amber-600" />
                Action Items
              </h3>
              {openTasks.length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                  {openTasks.length} open
                </span>
              )}
            </div>
            <Card className="p-0 overflow-hidden">
              <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                {allTasks.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm">
                    <CheckSquare className="w-8 h-8 mx-auto mb-2 text-gray-200" />
                    <p>No action items yet</p>
                  </div>
                ) : (
                  allTasks.slice(0, 6).map(task => (
                    <TaskItem 
                      key={task.id} 
                      task={task} 
                      meeting={task.meeting}
                      onMeetingClick={onSelectMeeting}
                    />
                  ))
                )}
              </div>
              {allTasks.length > 6 && (
                <div className="p-2 bg-gray-50 text-center border-t border-gray-100">
                  <button 
                    className="text-xs text-indigo-600 font-medium hover:text-indigo-800"
                    onClick={() => onNavigate?.('NOTES')}
                  >
                    View all {allTasks.length} tasks
                  </button>
                </div>
              )}
            </Card>
          </div>

        </div>

      </div>
    </div>
  );
};

export default DiaryDashboard;